import type { Command, CommandResult, SimulationState, ThemeData, GameSpeed } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';
import { createProject } from '../entities/ProjectFactory';
import { createTeam } from '../entities/TeamFactory';
import { getOffice, getMaxOfficeLevel } from '../entities/OfficeFactory';

export function processCommand(
  state: SimulationState,
  command: Command,
  themeData: ThemeData,
  rng: SeededRandom,
): { result: CommandResult; events: GameEvent[] } {
  const events: GameEvent[] = [];

  switch (command.type) {
    case 'SET_SPEED': {
      const validSpeeds: GameSpeed[] = [0, 1, 2, 4];
      if (!validSpeeds.includes(command.speed)) {
        return { result: { success: false, error: `Invalid speed: ${command.speed}` }, events };
      }
      state.speed = command.speed;
      state.isPaused = command.speed === 0;
      return { result: { success: true }, events };
    }

    case 'HIRE_STAFF': {
      const candidate = state.hiringPool.find((s) => s.id === command.staffId);
      if (!candidate) {
        return { result: { success: false, error: 'Candidate not found' }, events };
      }
      if (state.staff.length >= state.office.maxStaff) {
        return { result: { success: false, error: 'Office is full. Upgrade to hire more.' }, events };
      }
      // Hiring cost = 2x monthly salary as signing bonus
      const hiringCost = candidate.salary * 2;
      if (state.company.funds < hiringCost) {
        return { result: { success: false, error: `Insufficient funds. Need $${hiringCost}.` }, events };
      }

      state.company.funds -= hiringCost;
      state.company.totalExpenses += hiringCost;
      state.hiringPool = state.hiringPool.filter((s) => s.id !== command.staffId);
      state.staff.push(candidate);

      events.push({ type: 'STAFF_HIRED', staffId: candidate.id, name: candidate.name, role: candidate.role });
      events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -hiringCost, reason: `Hired ${candidate.name}` });
      return { result: { success: true }, events };
    }

    case 'FIRE_STAFF': {
      const staff = state.staff.find((s) => s.id === command.staffId);
      if (!staff) {
        return { result: { success: false, error: 'Staff member not found' }, events };
      }
      if (staff.isCEO) {
        return { result: { success: false, error: 'Cannot fire the founder.' }, events };
      }

      // Remove from team
      if (staff.teamId) {
        const team = state.teams.find((t) => t.id === staff.teamId);
        if (team) {
          team.memberIds = team.memberIds.filter((id) => id !== staff.id);
          if (team.leaderId === staff.id) team.leaderId = null;
        }
      }

      // Remove from contracts
      for (const contract of state.contracts) {
        contract.assignedStaffIds = contract.assignedStaffIds.filter((id) => id !== staff.id);
      }

      state.staff = state.staff.filter((s) => s.id !== command.staffId);
      events.push({ type: 'STAFF_FIRED', staffId: staff.id, name: staff.name });
      return { result: { success: true }, events };
    }

    case 'TRAIN_STAFF': {
      const staff = state.staff.find((s) => s.id === command.staffId);
      if (!staff) {
        return { result: { success: false, error: 'Staff member not found' }, events };
      }
      if (staff.isTraining) {
        return { result: { success: false, error: `${staff.name} is already training.` }, events };
      }
      if (staff.isOnLeave) {
        return { result: { success: false, error: `${staff.name} is currently on leave.` }, events };
      }
      if (staff.trainCooldownTicks > 0) {
        return { result: { success: false, error: `${staff.name} has trained recently. Wait for cooldown.` }, events };
      }

      const training = themeData.entities.trainingOptions.find((t) => t.id === command.trainingId);
      if (!training) {
        return { result: { success: false, error: 'Training option not found' }, events };
      }
      if (staff.level < training.minLevel) {
        return { result: { success: false, error: `${staff.name} needs to be level ${training.minLevel}+.` }, events };
      }
      if (state.company.funds < training.cost) {
        return { result: { success: false, error: `Insufficient funds. Need $${training.cost}.` }, events };
      }

      state.company.funds -= training.cost;
      state.company.totalExpenses += training.cost;
      staff.isTraining = true;
      staff.trainingTicksRemaining = training.duration;
      staff.trainingTarget = training.targetStat;

      // Remove from team while training
      if (staff.teamId) {
        const team = state.teams.find((t) => t.id === staff.teamId);
        if (team) {
          team.memberIds = team.memberIds.filter((id) => id !== staff.id);
          if (team.leaderId === staff.id) team.leaderId = null;
        }
        staff.teamId = null;
      }

      events.push({ type: 'TRAINING_STARTED', staffId: staff.id, name: staff.name, trainingId: command.trainingId });
      events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -training.cost, reason: `Training: ${training.name}` });
      return { result: { success: true }, events };
    }

    case 'PROMOTE_STAFF': {
      const staff = state.staff.find((s) => s.id === command.staffId);
      if (!staff) {
        return { result: { success: false, error: 'Staff member not found' }, events };
      }

      // Senior role promotion
      if (command.seniorRoleId) {
        const roleDef = themeData.entities.seniorRoles?.find((r) => r.id === command.seniorRoleId);
        if (!roleDef) {
          return { result: { success: false, error: 'Senior role not found.' }, events };
        }
        if (staff.level < roleDef.minLevel) {
          return { result: { success: false, error: `${staff.name} needs level ${roleDef.minLevel}+.` }, events };
        }
        staff.seniorRole = roleDef.id;
        staff.salary = Math.round(staff.salary * roleDef.salaryMultiplier);
        staff.desiredSalary = staff.salary;
      } else {
        staff.salary = Math.round(staff.salary * 1.2);
        staff.desiredSalary = staff.salary;
      }

      staff.satisfaction = Math.min(100, staff.satisfaction + 40);
      staff.morale = Math.min(100, staff.morale + 20);
      if (staff.demand?.type === 'promotion') {
        staff.demand = null;
        staff.isOnNotice = false;
        staff.noticeDaysRemaining = 0;
      }
      return { result: { success: true }, events };
    }

    case 'GIVE_RAISE': {
      const staff = state.staff.find((s) => s.id === command.staffId);
      if (!staff) {
        return { result: { success: false, error: 'Staff member not found' }, events };
      }
      if (command.newSalary <= staff.salary) {
        return { result: { success: false, error: 'New salary must be higher than current.' }, events };
      }
      staff.salary = command.newSalary;
      staff.desiredSalary = Math.min(staff.desiredSalary, command.newSalary);
      staff.satisfaction = Math.min(100, staff.satisfaction + 20);
      if (staff.demand?.type === 'raise') {
        staff.demand = null;
        staff.isOnNotice = false;
        staff.noticeDaysRemaining = 0;
      }
      return { result: { success: true }, events };
    }

    case 'GRANT_LEAVE': {
      const staff = state.staff.find((s) => s.id === command.staffId);
      if (!staff) {
        return { result: { success: false, error: 'Staff member not found' }, events };
      }
      if (staff.isOnLeave) {
        return { result: { success: false, error: `${staff.name} is already on leave.` }, events };
      }
      staff.isOnLeave = true;
      staff.leaveTicksRemaining = 70 * 2; // 2 weeks

      // Remove from team
      if (staff.teamId) {
        const team = state.teams.find((t) => t.id === staff.teamId);
        if (team) {
          team.memberIds = team.memberIds.filter((id) => id !== staff.id);
          if (team.leaderId === staff.id) team.leaderId = null;
        }
        staff.teamId = null;
      }

      if (staff.demand?.type === 'less_workload') {
        staff.demand = null;
        staff.isOnNotice = false;
        staff.noticeDaysRemaining = 0;
      }

      events.push({ type: 'LEAVE_STARTED', staffId: staff.id, name: staff.name });
      return { result: { success: true }, events };
    }

    case 'RESPOND_TO_DEMAND': {
      const staff = state.staff.find((s) => s.id === command.staffId);
      if (!staff) {
        return { result: { success: false, error: 'Staff member not found' }, events };
      }
      if (!staff.demand) {
        return { result: { success: false, error: `${staff.name} has no active demand.` }, events };
      }

      const demand = staff.demand;
      events.push({ type: 'STAFF_DEMAND_RESOLVED', staffId: staff.id, name: staff.name, response: command.response });

      if (command.response === 'accept') {
        staff.morale = Math.max(staff.morale, 70);
        staff.satisfaction = Math.min(100, staff.satisfaction + 30);
        staff.demand = null;
        staff.isOnNotice = false;
        staff.noticeDaysRemaining = 0;

        // Apply demand-specific costs
        switch (demand.type) {
          case 'raise':
            if (demand.requiredValue) {
              staff.salary = demand.requiredValue;
              staff.desiredSalary = demand.requiredValue;
            }
            break;
          case 'better_office': {
            const cost = 2000 + state.office.level * 2000;
            state.company.funds -= cost;
            state.company.totalExpenses += cost;
            events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -cost, reason: 'Office improvement' });
            break;
          }
          case 'less_workload':
            staff.isOnLeave = true;
            staff.leaveTicksRemaining = 70 * 2;
            if (staff.teamId) {
              const team = state.teams.find((t) => t.id === staff.teamId);
              if (team) {
                team.memberIds = team.memberIds.filter((id) => id !== staff.id);
                if (team.leaderId === staff.id) team.leaderId = null;
              }
              staff.teamId = null;
            }
            events.push({ type: 'LEAVE_STARTED', staffId: staff.id, name: staff.name });
            break;
          case 'promotion':
            staff.salary = Math.round(staff.salary * 1.2);
            staff.desiredSalary = staff.salary;
            break;
          case 'new_equipment': {
            const equipCost = 1000 + state.office.level * 1000;
            state.company.funds -= equipCost;
            state.company.totalExpenses += equipCost;
            staff.equipmentBonus = 1.05;
            staff.equipmentTicksRemaining = 70 * 20;
            events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -equipCost, reason: `Equipment for ${staff.name}` });
            break;
          }
        }
      } else if (command.response === 'negotiate') {
        staff.morale = Math.max(staff.morale, 50);
        staff.satisfaction = Math.min(100, staff.satisfaction + 15);
        staff.demand = null;
        staff.isOnNotice = false;
        staff.noticeDaysRemaining = 0;

        // 50% costs
        switch (demand.type) {
          case 'raise':
            if (demand.requiredValue) {
              const halfway = Math.round((staff.salary + demand.requiredValue) / 2);
              staff.salary = halfway;
              staff.desiredSalary = demand.requiredValue; // Still wants full amount
            }
            break;
          case 'better_office': {
            const halfCost = Math.round((2000 + state.office.level * 2000) / 2);
            state.company.funds -= halfCost;
            state.company.totalExpenses += halfCost;
            events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -halfCost, reason: 'Partial office improvement' });
            break;
          }
          case 'less_workload':
            staff.isOnLeave = true;
            staff.leaveTicksRemaining = 70; // 1 week instead of 2
            if (staff.teamId) {
              const team = state.teams.find((t) => t.id === staff.teamId);
              if (team) {
                team.memberIds = team.memberIds.filter((id) => id !== staff.id);
                if (team.leaderId === staff.id) team.leaderId = null;
              }
              staff.teamId = null;
            }
            events.push({ type: 'LEAVE_STARTED', staffId: staff.id, name: staff.name });
            break;
          case 'promotion':
            staff.salary = Math.round(staff.salary * 1.1);
            break;
          case 'new_equipment': {
            const halfEquipCost = Math.round((1000 + state.office.level * 1000) / 2);
            state.company.funds -= halfEquipCost;
            state.company.totalExpenses += halfEquipCost;
            staff.equipmentBonus = 1.03;
            staff.equipmentTicksRemaining = 70 * 10;
            events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -halfEquipCost, reason: `Partial equipment for ${staff.name}` });
            break;
          }
        }
      } else {
        // deny
        staff.morale = Math.max(0, staff.morale - 25);
        staff.satisfaction = Math.max(0, staff.satisfaction - 20);
        staff.isOnNotice = true;
        staff.noticeDaysRemaining = themeData.balance.departureNoticeDays;
        staff.demand = null;
        events.push({ type: 'STAFF_ON_NOTICE', staffId: staff.id, name: staff.name, daysRemaining: staff.noticeDaysRemaining });
      }
      return { result: { success: true }, events };
    }

    case 'START_PROJECT': {
      const activeProjects = state.projects.filter((p) => p.phase !== 'complete');
      if (activeProjects.length >= state.office.maxConcurrentProjects) {
        return { result: { success: false, error: 'Maximum concurrent projects reached. Upgrade office.' }, events };
      }
      const team = state.teams.find((t) => t.id === command.params.teamId);
      if (!team) {
        return { result: { success: false, error: 'Team not found' }, events };
      }
      if (team.memberIds.length === 0) {
        return { result: { success: false, error: 'Team has no members.' }, events };
      }
      if (team.assignedProjectId) {
        return { result: { success: false, error: 'Team is already working on a project.' }, events };
      }

      // Pull team members off active contracts so they can work on the project
      for (const memberId of team.memberIds) {
        for (const contract of state.contracts) {
          if (!contract.isActive) continue;
          contract.assignedStaffIds = contract.assignedStaffIds.filter((id) => id !== memberId);
          contract.lockedStaffIds = contract.lockedStaffIds.filter((id) => id !== memberId);
        }
      }

      // Validate marketing strategy exists and is affordable
      const strategy = themeData.entities.marketingStrategies.find((s) => s.id === command.params.marketingStrategyId);
      if (!strategy) {
        return { result: { success: false, error: 'Marketing strategy not found' }, events };
      }
      if (strategy.cost > 0 && state.company.funds < strategy.cost) {
        return { result: { success: false, error: `Insufficient funds for marketing. Need $${strategy.cost}.` }, events };
      }

      // Deduct marketing cost
      if (strategy.cost > 0) {
        state.company.funds -= strategy.cost;
        state.company.totalExpenses += strategy.cost;
        events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -strategy.cost, reason: `Marketing: ${strategy.name}` });
      }

      // Deduct platform license costs for all selected platforms
      const selectedPlatforms = command.params.platforms?.length ? command.params.platforms : [command.params.platform];
      let totalLicenseCost = 0;
      for (const pid of selectedPlatforms) {
        const pDef = themeData.entities.platforms.find((p) => p.id === pid);
        if (pDef) totalLicenseCost += pDef.licenseCost;
      }
      if (totalLicenseCost > 0) {
        if (state.company.funds < totalLicenseCost) {
          return { result: { success: false, error: `Need $${totalLicenseCost} for platform licenses.` }, events };
        }
        state.company.funds -= totalLicenseCost;
        state.company.totalExpenses += totalLicenseCost;
        const platNames = selectedPlatforms.map((pid) => themeData.entities.platforms.find((p) => p.id === pid)?.name ?? pid).join(', ');
        events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -totalLicenseCost, reason: `Licenses: ${platNames}` });
      }

      const project = createProject(command.params, themeData, rng);
      state.projects.push(project);
      team.assignedProjectId = project.id;

      events.push({ type: 'PROJECT_STARTED', projectId: project.id, name: project.name, category: project.category, size: project.size });
      return { result: { success: true }, events };
    }

    case 'CANCEL_PROJECT': {
      const project = state.projects.find((p) => p.id === command.projectId);
      if (!project) {
        return { result: { success: false, error: 'Project not found' }, events };
      }
      if (project.phase === 'complete') {
        return { result: { success: false, error: 'Project is already complete.' }, events };
      }

      // Free the team
      const team = state.teams.find((t) => t.assignedProjectId === command.projectId);
      if (team) {
        team.assignedProjectId = null;
      }

      state.projects = state.projects.filter((p) => p.id !== command.projectId);
      events.push({ type: 'PROJECT_CANCELLED', projectId: project.id, name: project.name });
      return { result: { success: true }, events };
    }

    case 'RENAME_PROJECT': {
      const project = state.projects.find((p) => p.id === command.projectId);
      if (!project) {
        return { result: { success: false, error: 'Project not found' }, events };
      }
      project.name = command.name;
      return { result: { success: true }, events };
    }

    case 'SHIP_PROJECT': {
      const project = state.projects.find((p) => p.id === command.projectId);
      if (!project) {
        return { result: { success: false, error: 'Project not found' }, events };
      }
      if (project.phase !== 'ready' && project.phase !== 'debug' && project.phase !== 'polish') {
        return { result: { success: false, error: 'Project is not ready to ship.' }, events };
      }

      // Factor bugs into quality
      const bugPenalty = Math.min(project.bugs * 0.5, 30);
      project.quality = Math.max(0, project.quality - bugPenalty);
      project.phase = 'complete';
      project.phaseProgress = 100;

      // Free the team
      const team = state.teams.find((t) => t.assignedProjectId === project.id);
      if (team) team.assignedProjectId = null;

      events.push({ type: 'PROJECT_COMPLETED', projectId: project.id, name: project.name });
      return { result: { success: true }, events };
    }

    case 'DEBUG_MORE': {
      const project = state.projects.find((p) => p.id === command.projectId);
      if (!project) {
        return { result: { success: false, error: 'Project not found' }, events };
      }
      if (project.phase !== 'ready') {
        return { result: { success: false, error: 'Project is not in ready state.' }, events };
      }

      // Send back to debug phase — increment cycle counter so progress target extends
      project.phase = 'debug';
      project.ticksInPhase = 0;
      project.phaseProgress = 0;
      project.debugCycles = (project.debugCycles ?? 0) + 1;

      events.push({ type: 'PHASE_CHANGED', projectId: project.id, name: project.name, newPhase: 'debug' });
      return { result: { success: true }, events };
    }

    case 'CREATE_TEAM': {
      if (state.teams.length >= state.office.maxTeams) {
        return { result: { success: false, error: 'Maximum teams reached. Upgrade office.' }, events };
      }
      const team = createTeam(command.name);
      state.teams.push(team);
      events.push({ type: 'TEAM_CREATED', teamId: team.id, name: team.name });
      return { result: { success: true }, events };
    }

    case 'DISBAND_TEAM': {
      const teamIdx = state.teams.findIndex((t) => t.id === command.teamId);
      if (teamIdx === -1) {
        return { result: { success: false, error: 'Team not found' }, events };
      }
      const team = state.teams[teamIdx]!;
      if (team.assignedProjectId) {
        return { result: { success: false, error: 'Cannot disband a team with an active project.' }, events };
      }
      // Unassign all members
      for (const memberId of team.memberIds) {
        const member = state.staff.find((s) => s.id === memberId);
        if (member) member.teamId = null;
      }
      // Remove exactly this one team (splice, not filter — safe if IDs ever collide)
      state.teams.splice(teamIdx, 1);
      events.push({ type: 'TEAM_DISBANDED', teamId: team.id, name: team.name });
      return { result: { success: true }, events };
    }

    case 'ASSIGN_TO_TEAM': {
      const staff = state.staff.find((s) => s.id === command.staffId);
      if (!staff) {
        return { result: { success: false, error: 'Staff member not found' }, events };
      }
      if (staff.isTraining || staff.isOnLeave) {
        return { result: { success: false, error: `${staff.name} is unavailable.` }, events };
      }
      const team = state.teams.find((t) => t.id === command.teamId);
      if (!team) {
        return { result: { success: false, error: 'Team not found' }, events };
      }
      // Count real members (exclude stale IDs) without mutating yet
      const staffIds = new Set(state.staff.map((s) => s.id));
      const realMemberCount = team.memberIds.filter((id) => staffIds.has(id)).length;
      if (realMemberCount >= team.maxSize) {
        return { result: { success: false, error: 'Team is full.' }, events };
      }

      // Now safe to commit — clean stale IDs
      team.memberIds = team.memberIds.filter((id) => staffIds.has(id));

      // Remove from current team if any
      if (staff.teamId) {
        const oldTeam = state.teams.find((t) => t.id === staff.teamId);
        if (oldTeam) {
          oldTeam.memberIds = oldTeam.memberIds.filter((id) => id !== staff.id);
          if (oldTeam.leaderId === staff.id) oldTeam.leaderId = null;
        }
      }

      staff.teamId = team.id;
      if (!team.memberIds.includes(staff.id)) {
        team.memberIds.push(staff.id);
      }
      return { result: { success: true }, events };
    }

    case 'REMOVE_FROM_TEAM': {
      const staff = state.staff.find((s) => s.id === command.staffId);
      if (!staff) {
        return { result: { success: false, error: 'Staff member not found' }, events };
      }
      if (!staff.teamId) {
        return { result: { success: false, error: `${staff.name} is not on a team.` }, events };
      }
      const team = state.teams.find((t) => t.id === staff.teamId);
      if (team) {
        team.memberIds = team.memberIds.filter((id) => id !== staff.id);
        if (team.leaderId === staff.id) team.leaderId = null;
      }
      staff.teamId = null;
      return { result: { success: true }, events };
    }

    case 'SET_TEAM_LEAD': {
      const team = state.teams.find((t) => t.id === command.teamId);
      if (!team) {
        return { result: { success: false, error: 'Team not found' }, events };
      }
      if (!team.memberIds.includes(command.staffId)) {
        return { result: { success: false, error: 'Staff member is not on this team.' }, events };
      }
      team.leaderId = command.staffId;
      return { result: { success: true }, events };
    }

    case 'UPGRADE_OFFICE': {
      const maxLevel = getMaxOfficeLevel(themeData);
      if (state.office.level >= maxLevel) {
        return { result: { success: false, error: 'Already at maximum office level.' }, events };
      }
      if (state.company.funds < state.office.upgradeCost) {
        return { result: { success: false, error: `Insufficient funds. Need $${state.office.upgradeCost}.` }, events };
      }

      const cost = state.office.upgradeCost;
      state.company.funds -= cost;
      state.company.totalExpenses += cost;
      state.office = getOffice(state.office.level + 1, themeData);

      events.push({ type: 'OFFICE_UPGRADED', newLevel: state.office.level, newName: state.office.name });
      events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -cost, reason: `Office upgrade: ${state.office.name}` });
      return { result: { success: true }, events };
    }

    case 'ACCEPT_CONTRACT': {
      const contract = state.contractPool.find((c) => c.id === command.contractId);
      if (!contract) {
        return { result: { success: false, error: 'Contract not found' }, events };
      }

      contract.isActive = true;
      state.contractPool = state.contractPool.filter((c) => c.id !== command.contractId);
      state.contracts.push(contract);

      // Immediately assign idle staff so the UI doesn't show "paused" on first render
      if (command.staffIds.length > 0) {
        contract.assignedStaffIds = [...command.staffIds];
        contract.lockedStaffIds = [...command.staffIds];
        contract.assignmentMode = 'specific';
      } else {
        // Find currently idle staff for initial assignment
        const alreadyOnContracts = new Set(
          state.contracts.flatMap((c) => c.id !== contract.id ? c.assignedStaffIds : []),
        );
        const idle = state.staff.filter((s) => {
          if (s.isTraining || s.isOnLeave) return false;
          if (alreadyOnContracts.has(s.id)) return false;
          if (s.teamId && state.teams.some((t) => t.id === s.teamId && t.assignedProjectId)) return false;
          return true;
        });
        contract.assignedStaffIds = idle.slice(0, contract.requiredStaff).map((s) => s.id);
      }

      events.push({ type: 'CONTRACT_ACCEPTED', contractId: contract.id, clientName: contract.clientName });
      return { result: { success: true }, events };
    }

    case 'RESPOND_TO_EVENT': {
      // Events with choices are handled by the EventSystem
      // This command just records the player's choice — the EventSystem processes it on the next tick
      // For now, just acknowledge it
      return { result: { success: true }, events };
    }

    case 'SET_MARKETING': {
      const project = state.projects.find((p) => p.id === command.projectId);
      if (!project) {
        return { result: { success: false, error: 'Project not found' }, events };
      }
      if (project.phase !== 'planning') {
        return { result: { success: false, error: 'Can only change marketing during planning phase.' }, events };
      }
      const strategy = themeData.entities.marketingStrategies.find((s) => s.id === command.strategyId);
      if (!strategy) {
        return { result: { success: false, error: 'Marketing strategy not found' }, events };
      }
      project.marketingStrategyId = command.strategyId;
      return { result: { success: true }, events };
    }

    case 'BUY_EQUIPMENT': {
      const equipDef = themeData.entities.equipment.find((e) => e.id === command.equipmentId);
      if (!equipDef) {
        return { result: { success: false, error: 'Equipment not found.' }, events };
      }
      if (!state.purchasedEquipment) state.purchasedEquipment = [];
      if (state.purchasedEquipment.includes(command.equipmentId)) {
        return { result: { success: false, error: `Already own ${equipDef.name}.` }, events };
      }
      if (state.company.funds < equipDef.cost) {
        return { result: { success: false, error: `Need ${equipDef.cost} to buy ${equipDef.name}.` }, events };
      }
      state.company.funds -= equipDef.cost;
      state.company.totalExpenses += equipDef.cost;
      state.purchasedEquipment.push(command.equipmentId);

      // Recalculate best equipment bonus for all staff from all owned equipment
      const allEquip = themeData.entities.equipment.filter((e) => state.purchasedEquipment.includes(e.id));
      for (const staff of state.staff) {
        let bestBonus = 1.0;
        for (const eq of allEquip) {
          if (eq.stat === '*' || Object.keys(staff.stats).includes(eq.stat)) {
            if (eq.bonus > bestBonus) {
              bestBonus = eq.bonus;
            }
          }
        }
        staff.equipmentBonus = bestBonus;
        // Permanent equipment: set a very long timer so it never expires
        if (bestBonus > 1.0) staff.equipmentTicksRemaining = 999999;
      }

      events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -equipDef.cost, reason: `Equipment: ${equipDef.name}` });
      return { result: { success: true }, events };
    }

    case 'HIRE_RECRUITER': {
      if (state.activeRecruiter) {
        return { result: { success: false, error: 'A recruiter is already searching.' }, events };
      }
      const recruiterDef = themeData.entities.recruiters.find((r) => r.id === command.tier);
      if (!recruiterDef) {
        return { result: { success: false, error: 'Recruiter tier not found.' }, events };
      }
      if (state.company.funds < recruiterDef.cost) {
        return { result: { success: false, error: `Insufficient funds. Need ${recruiterDef.cost}.` }, events };
      }
      state.company.funds -= recruiterDef.cost;
      state.company.totalExpenses += recruiterDef.cost;
      state.hiringPool = []; // Clear old candidates
      state.activeRecruiter = {
        tier: recruiterDef.id,
        ticksRemaining: recruiterDef.durationTicks,
        cost: recruiterDef.cost,
      };
      events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -recruiterDef.cost, reason: `Recruiter: ${recruiterDef.name}` });
      return { result: { success: true }, events };
    }

    case 'TAKE_LOAN': {
      // Max 3 active loans
      if (state.loans.length >= 3) {
        return { result: { success: false, error: 'Maximum 3 active loans.' }, events };
      }
      const amount = command.amount;
      const termWeeks = command.termWeeks;
      if (amount <= 0 || termWeeks <= 0) {
        return { result: { success: false, error: 'Invalid loan terms.' }, events };
      }
      // Interest rate: higher for longer terms and larger amounts, harder in hard mode
      const baseRate = state.company.isHardMode ? 0.12 : 0.06;
      const interestRate = baseRate + (termWeeks > 26 ? 0.02 : 0) + (amount > 50000 ? 0.02 : 0);
      const totalOwed = Math.round(amount * (1 + interestRate * (termWeeks / 52)));
      const weeklyPayment = Math.ceil(totalOwed / termWeeks);

      const loan: import('../types').Loan = {
        id: `loan_${Date.now().toString(36)}`,
        amount,
        interestRate,
        weeklyPayment,
        remaining: totalOwed,
        totalPaid: 0,
        weeksLeft: termWeeks,
      };
      state.loans.push(loan);
      state.company.funds += amount;
      events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: amount, reason: `Loan: $${amount}` });
      return { result: { success: true }, events };
    }

    case 'REPAY_LOAN': {
      const loan = state.loans.find((l) => l.id === command.loanId);
      if (!loan) {
        return { result: { success: false, error: 'Loan not found.' }, events };
      }
      if (state.company.funds < loan.remaining) {
        return { result: { success: false, error: `Need ${loan.remaining} to pay off this loan.` }, events };
      }
      state.company.funds -= loan.remaining;
      state.company.totalExpenses += loan.remaining;
      loan.totalPaid += loan.remaining;
      state.loans = state.loans.filter((l) => l.id !== command.loanId);
      events.push({ type: 'FUNDS_CHANGED', newFunds: state.company.funds, delta: -loan.remaining, reason: 'Loan repaid early' });
      return { result: { success: true }, events };
    }

    case 'NEW_GAME':
    case 'START_NEW_GAME_PLUS': {
      // These are handled at a higher level, not by the command processor
      return { result: { success: false, error: 'Use the game initialization flow instead.' }, events };
    }

    default: {
      // Exhaustive check — TypeScript will error here if a command type is missing
      const _exhaustive: never = command;
      return { result: { success: false, error: `Unknown command: ${(_exhaustive as Command).type}` }, events };
    }
  }
}
